import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ArrowRight, Microscope, CheckCircle, Truck, Users, Loader2, Star, MessageCircle, Lock, Globe, LogIn, TrendingDown, Heart, Brain, Zap, Flame, Layers } from 'lucide-react';
import { useAuth } from '../context/AuthContext.jsx';
import { useCart } from '../context/CartContext.jsx';
import { useLanguage } from '../context/LanguageContext.jsx';
import AuthModal from '../components/AuthModal';
import { toast } from 'sonner';
import SEO, { organizationSchema, websiteSchema } from '../components/SEO';
import { features } from '../data/mock';
import { getProductSlug, getProductUrlSlug } from '../data/seoConfig';
import { optimizedImageProps } from '../utils/imageUrl';
import HomeNewSections from '../components/HomeNewSections';
import HomePromotions from '../components/HomePromotions';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Star Rating Component
const StarRating = ({ rating, size = 18 }) => (
  <div className="flex gap-1">
    {[1, 2, 3, 4, 5].map((star) => (
      <Star
        key={star}
        size={size}
        fill={rating >= star ? '#f59e0b' : 'transparent'}
        color={rating >= star ? '#f59e0b' : '#d1d5db'}
      />
    ))}
  </div>
);

const Home = () => {
  const [featuredProducts, setFeaturedProducts] = useState([]);
  const [featuredCatNos, setFeaturedCatNos] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [reviews, setReviews] = useState([]);
  const [reviewStats, setReviewStats] = useState({ average_rating: 0, total_reviews: 0 });
  const [chatRooms, setChatRooms] = useState([]);
  const [researchStacks, setResearchStacks] = useState([]);
  const [allProducts, setAllProducts] = useState([]);
  const [addedStacks, setAddedStacks] = useState({});
  const { t } = useLanguage();
  const [showAuthModal, setShowAuthModal] = useState(false);
  const { user, isAuthenticated } = useAuth();
  const { addToCart } = useCart();
  const navigate = useNavigate();

  useEffect(() => {
    const fetchProducts = async () => {
      try {
        const [productsRes, featuredRes] = await Promise.all([
          fetch(`${API_URL}/api/products`),
          fetch(`${API_URL}/api/settings/featured-products`)
        ]);
        if (productsRes.ok) {
          const products = await productsRes.json();
          let featuredCatNos = ['SM30', 'TR60', 'BC10'];
          if (featuredRes.ok) {
            const data = await featuredRes.json();
            if (data.featured?.length) featuredCatNos = data.featured;
          }
          const featured = featuredCatNos
            .map(catNo => products.find(p => p.catNo === catNo))
            .filter(Boolean);
          setFeaturedProducts(featured);
          setFeaturedCatNos(featuredCatNos);
          setAllProducts(products);
        }
      } catch (error) {
        console.error('Error fetching products:', error);
      } finally {
        setIsLoading(false);
      }
    };
    
    const fetchReviews = async () => {
      try {
        const [reviewsRes, statsRes] = await Promise.all([
          fetch(`${API_URL}/api/reviews`),
          fetch(`${API_URL}/api/reviews/stats`)
        ]);
        if (reviewsRes.ok) {
          const data = await reviewsRes.json();
          setReviews(data.slice(0, 3)); // Get top 3 reviews
        }
        if (statsRes.ok) {
          const stats = await statsRes.json();
          setReviewStats(stats);
        }
      } catch (error) {
        console.error('Error fetching reviews:', error);
      }
    };
    
    fetchProducts();
    fetchReviews();

    const fetchChatRooms = async () => {
      try {
        const res = await fetch(`${API_URL}/api/chat/rooms`);
        if (res.ok) {
          const data = await res.json();
          setChatRooms(data.slice(0, 4));
        }
      } catch (err) {
        console.error('Error fetching chat rooms:', err);
      }
    };
    fetchChatRooms();

    const fetchStacks = async () => {
      try {
        const res = await fetch(`${API_URL}/api/stacks`);
        if (res.ok) {
          const data = await res.json();
          setResearchStacks(data);
        }
      } catch (err) {
        console.error('Error fetching stacks:', err);
      }
    };
    fetchStacks();
  }, []);

  const iconMap = {
    Microscope: Microscope,
    CheckCircle: CheckCircle,
    Truck: Truck,
    Users: Users
  };

  const stackIconMap = {
    TrendingDown, Heart, Brain, Zap, Flame, Layers
  };

  return (
    <div>
      <SEO
        title="Research Peptides for Sale USA"
        description="Shop laboratory research peptides from a U.S.-based supplier with batch-specific COAs and third-party purity and identity testing. Research use only."
        path="/"
        schema={[organizationSchema, websiteSchema]}
      />
      {/* Hero Section - Centered Radial */}
      <section className="rcm-hero" style={{ position: 'relative', overflow: 'hidden' }}>
        {/* Background video — GLP-1 hero (Tirzepatide → the retired compound (center)
         *  → Semaglutide vials). Placed BEHIND all text with a dark
         *  gradient overlay so the existing white heading/subtitle/CTA
         *  buttons stay readable. Autoplay muted+inline+loop is the
         *  standard mobile-friendly recipe. preload=metadata keeps LCP
         *  fast (~200KB header only, not the whole 1.5MB file). The
         *  poster JPG is the first frame so slow connections and
         *  reduced-motion users see the same composition.
         *
         *  object-position: 50% 50% keeps the CENTER of the frame
         *  (the retired compound vial) locked as the viewport narrows on mobile —
         *  the sides crop first, matching the 2026-08-02 brief. */}
        <video
          className="rcm-hero-video"
          data-testid="home-hero-video"
          autoPlay
          muted
          loop
          playsInline
          preload="metadata"
          poster="/videos/rcm-glp1-homepage-hero-poster.jpg"
          aria-hidden="true"
          style={{
            position: 'absolute',
            top: 0, left: 0,
            width: '100%', height: '100%',
            objectFit: 'cover',
            objectPosition: '50% 50%',
            zIndex: 0,
            // A poster is shown when the browser respects
            // `prefers-reduced-motion: reduce` (see CSS block below);
            // that CSS hides the <video> element and reveals the sibling
            // <img> poster instead. Native `<video>` doesn't consult the
            // reduced-motion media query on its own.
            pointerEvents: 'none',
          }}
        >
          <source src="/videos/rcm-glp1-homepage-hero.mp4" type="video/mp4" />
        </video>
        {/* Static poster shown to reduced-motion users AND while the
         * video buffers on slow connections. It sits UNDER the video
         * and is only visible when the video is hidden (see CSS). */}
        <img
          src="/videos/rcm-glp1-homepage-hero-poster.jpg"
          alt=""
          aria-hidden="true"
          className="rcm-hero-poster"
          style={{
            position: 'absolute', top: 0, left: 0,
            width: '100%', height: '100%',
            objectFit: 'cover', objectPosition: '50% 50%',
            zIndex: 0,
          }}
        />
        {/* Dark gradient overlay — preserves the previous look and
         * keeps hero heading + white subtitle + navy CTA button
         * readable against the bright vial photography. */}
        <div
          aria-hidden="true"
          style={{
            position: 'absolute', top: 0, left: 0, right: 0, bottom: 0,
            background: 'linear-gradient(180deg, rgba(2,11,20,0.72) 0%, rgba(2,11,20,0.55) 55%, rgba(2,11,20,0.82) 100%)',
            zIndex: 1,
          }}
        />
        {/* Reduced-motion + hero-video responsive rules. Scoped tightly
         * so nothing outside the hero is touched. */}
        <style>{`
          .rcm-hero-video { display: block; }
          @media (prefers-reduced-motion: reduce) {
            .rcm-hero-video { display: none !important; }
          }
          @media (max-width: 640px) {
            /* On phones the vials become tiny; nudge the framing up
             * slightly so the CENTER the retired compound vial's silver cap +
             * label stay comfortably in-frame instead of hugging the
             * horizontal midline. */
            .rcm-hero-video, .rcm-hero-poster { object-position: 50% 40% !important; }
          }
        `}</style>
        {/* Molecular Background Pattern (kept — sits on top of the
         * video overlay for the subtle atomic sparkle effect that was
         * on the original hero) */}
        <div 
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%2300E5FF' fill-opacity='0.04'%3E%3Ccircle cx='30' cy='30' r='4'/%3E%3Ccircle cx='10' cy='10' r='2'/%3E%3Ccircle cx='50' cy='50' r='2'/%3E%3Ccircle cx='50' cy='10' r='2'/%3E%3Ccircle cx='10' cy='50' r='2'/%3E%3Cpath d='M30 26 L10 10 M30 26 L50 10 M30 34 L10 50 M30 34 L50 50'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
            opacity: 0.6,
            zIndex: 2,
            pointerEvents: 'none',
          }}
        />
        
        <div className="container" style={{ position: 'relative', zIndex: 3 }}>
          <div style={{ maxWidth: '800px', margin: '0 auto' }}>
            <h1 
              style={{ 
                color: 'white',
                fontSize: 'clamp(2.5rem, 5vw, 4rem)',
                fontWeight: '700',
                letterSpacing: '0.03em',
                lineHeight: '1.1',
                marginBottom: '24px'
              }}
            >
              {t('home.heroHeading1')}<br />
              {t('home.heroHeading2')}
            </h1>
            
            <p 
              style={{ 
                color: 'rgba(255, 255, 255, 0.85)',
                fontSize: '1.125rem',
                lineHeight: '1.8',
                marginBottom: '40px',
                maxWidth: '600px',
                margin: '0 auto 40px'
              }}
            >
              {t('home.heroSubtitle')}
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 mb-8 justify-center">
              <Link 
                to="/contact" 
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '8px',
                  padding: '14px 28px',
                  background: '#00E5FF',
                  color: '#000',
                  fontWeight: '600',
                  fontSize: '0.95rem',
                  borderRadius: '10px',
                  textDecoration: 'none',
                  transition: 'all 0.3s ease',
                  letterSpacing: '0.02em',
                  boxShadow: '0 0 12px #00E5FF'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'scale(1.05)';
                  e.currentTarget.style.boxShadow = '0 0 20px #00E5FF';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'scale(1)';
                  e.currentTarget.style.boxShadow = '0 0 12px #00E5FF';
                }}
              >
                {t('home.submitRequest')}
                <ArrowRight size={18} />
              </Link>
              
              <Link 
                to="/how-to-order" 
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '8px',
                  padding: '14px 28px',
                  background: 'transparent',
                  color: '#00E5FF',
                  fontWeight: '600',
                  fontSize: '0.95rem',
                  borderRadius: '10px',
                  textDecoration: 'none',
                  border: '1px solid rgba(0, 229, 255, 0.4)',
                  transition: 'all 0.3s ease',
                  letterSpacing: '0.02em'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = 'rgba(0, 229, 255, 0.1)';
                  e.currentTarget.style.borderColor = 'rgba(0, 229, 255, 0.7)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = 'transparent';
                  e.currentTarget.style.borderColor = 'rgba(0, 229, 255, 0.4)';
                }}
              >
                {t('home.howPayment')}
              </Link>
            </div>
            
            {/* Trust Micro-Text */}
            <p 
              style={{ 
                color: 'rgba(255, 255, 255, 0.5)',
                fontSize: '0.8rem',
                letterSpacing: '0.1em',
                textTransform: 'uppercase'
              }}
            >
              {t('home.trustBadges')}
            </p>
            {/* Supporting trust line — U.S.-based supplier with domestic shipping.
                Rendered inside the existing hero trust area, not a new section. */}
            <p
              data-testid="home-hero-trust-line"
              style={{
                color: 'rgba(255, 255, 255, 0.55)',
                fontSize: '0.8rem',
                marginTop: '8px',
                letterSpacing: '0.02em',
              }}
            >
              U.S.-based research peptide supplier with domestic shipping available.
            </p>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="section-padding rcm-section reveal">
        <div className="container">
          <div className="text-center mb-12">
            <h2 className="hero-medium mb-4" style={{ color: 'var(--brand-primary)' }}>{t('home.whyChoose')}</h2>
            <p className="body-large" style={{ color: 'var(--text-secondary)', maxWidth: '600px', margin: '0 auto' }}>
              {t('home.whyChooseSubtitle')}
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {features.map((feature) => {
              const IconComponent = iconMap[feature.icon];
              return (
                <div key={feature.id} className="text-center">
                  <div className="flex justify-center mb-4">
                    <div 
                      style={{ 
                        width: '64px', 
                        height: '64px', 
                        background: 'rgba(0, 229, 255, 0.1)',
                        border: '1px solid rgba(0, 229, 255, 0.2)',
                        borderRadius: '12px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                      }}
                    >
                      <IconComponent size={32} style={{ color: '#00E5FF' }} />
                    </div>
                  </div>
                  <h3 className="heading-3 mb-3" style={{ color: 'var(--text-primary)' }}>{feature.title}</h3>
                  <p className="body-regular" style={{ color: 'var(--text-secondary)' }}>
                    {feature.description}
                  </p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Featured Products */}
      <section className="section-padding rcm-section-alt reveal">
        <div className="container">
          <div className="flex justify-between items-end mb-12">
            <div>
              <h2 className="hero-medium mb-4" style={{ color: 'var(--brand-primary)' }}>{t('home.featuredProducts')}</h2>
              <p className="body-large" style={{ color: 'var(--text-secondary)' }}>
                {t('home.featuredSubtitle')}
              </p>
            </div>
            <Link to="/products" className="btn-icon hidden md:flex">
              {t('home.viewAll')}
              <ArrowRight size={16} />
            </Link>
          </div>
          <div className="grid-product-showcase">
            {featuredProducts.map((product) => (
              <div key={product.id} className="rcm-card" style={{ padding: 0, overflow: 'hidden' }}>
                <div 
                  style={{ 
                    background: 'rgba(15, 46, 109, 0.3)', 
                    height: '300px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    padding: '24px'
                  }}
                >
                  {product.image ? (
                    <img 
                      {...optimizedImageProps(product.image, { sizes: '(max-width: 640px) 90vw, 320px', maxWidth: 640 })}
                      alt={`${product.name} ${product.dosage}`}
                      width="320"
                      height="320"
                      loading="lazy"
                      decoding="async"
                      style={{ width: '100%', height: '100%', objectFit: 'contain' }}
                      onError={(e) => { e.target.style.display = 'none'; e.target.nextSibling.style.display = 'flex'; }}
                    />
                  ) : null}
                  <div style={{ 
                    display: product.image ? 'none' : 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    textAlign: 'center',
                    gap: '8px'
                  }}>
                    <h3 style={{ color: '#fff', fontSize: '20px' }}>{product.name}</h3>
                    <p>{product.dosage}</p>
                  </div>
                </div>
                <div style={{ padding: '20px' }}>
                  <h3>{product.name} {product.dosage}</h3>
                  <p>Research Peptide — ${product.price1}</p>
                  <Link 
                    to={`/products/${getProductUrlSlug(product)}`} 
                    className="rcm-btn w-full"
                    style={{ marginTop: '16px' }}
                  >
                    Order Now
                  </Link>
                </div>
              </div>
            ))}
          </div>
          <div className="text-center mt-8 md:hidden">
            <Link to="/products" className="btn-primary">
              {t('home.viewAll')}
            </Link>
          </div>
        </div>
      </section>

      {/* Current Promotions — DB-driven, only renders when active promos exist.
          Sits between Featured Research Peptides and downstream homepage
          sections. Hides itself automatically if no active homepage-visible
          promos are returned by /api/promotions?surface=homepage. */}
      <HomePromotions />

      {/* New AEO/conversion sections — most requested, new arrivals, restocked,
          verify batch, comparison center, latest articles. See HomeNewSections.jsx. */}
      <HomeNewSections products={allProducts} featuredCatNos={featuredCatNos} />

      {/* Research Stacks Section */}
      {researchStacks.length > 0 && (
        <section className="section-padding rcm-section reveal" data-testid="research-stacks-section">
          <div className="container">
            <div className="text-center mb-12">
              <div 
                style={{ 
                  display: 'inline-flex', 
                  alignItems: 'center', 
                  gap: '10px', 
                  background: 'rgba(0, 229, 255, 0.08)', 
                  padding: '8px 20px', 
                  borderRadius: '24px', 
                  marginBottom: '16px',
                  border: '1px solid rgba(0, 229, 255, 0.15)'
                }}
              >
                <Layers size={18} style={{ color: '#00E5FF' }} />
                <span style={{ color: '#00E5FF', fontWeight: '600', fontSize: '14px', letterSpacing: '0.5px' }}>
                  CURATED FOR RESEARCH
                </span>
              </div>
              <h2 className="hero-medium mb-4" style={{ color: 'var(--brand-primary)' }}>
                Research Stacks
              </h2>
              <p className="body-large" style={{ color: 'var(--text-secondary)', maxWidth: '620px', margin: '0 auto' }}>
                Pre-selected compound combinations designed for specific research pathways. Each stack includes complementary peptides and essential supplies.
              </p>
            </div>

            <div 
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))',
                gap: '24px'
              }}
            >
              {researchStacks.map((stack) => {
                const StackIcon = stackIconMap[stack.icon] || Layers;
                return (
                  <div
                    key={stack.id}
                    className="rcm-card"
                    data-testid={`stack-card-${stack.id}`}
                    style={{ display: 'flex', flexDirection: 'column' }}
                  >
                    {/* Header */}
                    <div style={{ display: 'flex', alignItems: 'center', gap: '14px', marginBottom: '16px' }}>
                      <div
                        style={{
                          width: '48px',
                          height: '48px',
                          borderRadius: '12px',
                          background: 'rgba(0, 229, 255, 0.1)',
                          border: '1px solid rgba(0, 229, 255, 0.2)',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          flexShrink: 0
                        }}
                      >
                        <StackIcon size={24} style={{ color: '#00E5FF' }} />
                      </div>
                      <div>
                        <h3 style={{ color: '#FFFFFF', fontSize: '17px', fontWeight: '600', margin: 0, lineHeight: '1.3' }}>
                          {stack.name}
                        </h3>
                      </div>
                    </div>

                    {/* Description */}
                    <p style={{ color: 'var(--text-secondary)', fontSize: '14px', lineHeight: '1.6', marginBottom: '16px' }}>
                      {stack.description}
                    </p>

                    {/* Included Products */}
                    <div style={{ flex: 1, marginBottom: '16px' }}>
                      <p style={{ color: 'var(--text-light)', fontSize: '11px', textTransform: 'uppercase', letterSpacing: '1px', marginBottom: '10px', fontWeight: '600' }}>
                        Included Compounds
                      </p>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                        {stack.products.map((prod, idx) => (
                          <div 
                            key={idx}
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'space-between',
                              padding: '8px 12px',
                              background: 'rgba(0, 229, 255, 0.04)',
                              borderRadius: '8px',
                              border: '1px solid rgba(0, 229, 255, 0.08)'
                            }}
                          >
                            <span style={{ color: '#FFFFFF', fontSize: '13px', fontWeight: '500' }}>
                              {prod.name}
                            </span>
                            <span style={{ color: 'var(--text-light)', fontSize: '12px' }}>
                              {prod.dosage}
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* Use Case */}
                    {stack.use_case && (
                      <p style={{ 
                        color: 'var(--text-light)', 
                        fontSize: '12px', 
                        lineHeight: '1.5',
                        fontStyle: 'italic',
                        paddingTop: '12px',
                        borderTop: '1px solid rgba(0, 229, 255, 0.08)',
                        marginBottom: '16px'
                      }}>
                        {stack.use_case}
                      </p>
                    )}

                    {/* CTA */}
                    <button
                      onClick={() => {
                        let added = 0;
                        stack.products.forEach(sp => {
                          const prod = allProducts.find(p => p.catNo === sp.catNo);
                          if (prod) {
                            addToCart(prod, 1, 'single');
                            added++;
                          }
                        });
                        if (added > 0) {
                          toast.success(`${stack.name} added to cart (${added} items)`);
                          setAddedStacks(prev => ({ ...prev, [stack.id]: true }));
                          setTimeout(() => setAddedStacks(prev => ({ ...prev, [stack.id]: false })), 2500);
                        }
                      }}
                      className="rcm-btn w-full"
                      style={{
                        background: addedStacks[stack.id] ? '#10b981' : undefined,
                        boxShadow: addedStacks[stack.id] ? '0 0 12px #10b981' : undefined
                      }}
                      data-testid={`stack-add-cart-${stack.id}`}
                    >
                      {addedStacks[stack.id] ? 'Added to Cart' : 'Add Stack to Cart'}
                    </button>
                  </div>
                );
              })}
            </div>

            {/* Compliance */}
            <div className="text-center mt-10">
              <p 
                className="body-small" 
                style={{ 
                  color: 'var(--text-light)', 
                  fontStyle: 'italic',
                  maxWidth: '600px',
                  margin: '0 auto'
                }}
              >
                Stacks are curated suggestions for research protocols. Individual compounds sold separately.
              </p>
            </div>
          </div>
        </section>
      )}

      {/* Research Areas We Supply Section */}
      <section className="section-padding rcm-section-alt reveal" data-testid="research-areas-section">
        <div className="container">
          <div className="text-center mb-12">
            <h2 className="hero-medium mb-4" style={{ color: 'var(--brand-primary)' }}>
              {t('home.researchAreas')}
            </h2>
            <p className="body-large" style={{ color: 'var(--text-secondary)', maxWidth: '650px', margin: '0 auto' }}>
              {t('home.researchAreasSubtitle')}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {/* Tile 1: Metabolic Research */}
            <div 
              style={{ 
                background: 'var(--bg-secondary)', 
                border: '1px solid var(--border-light)',
                padding: '28px',
                borderTop: '4px solid var(--brand-primary)',
                transition: 'box-shadow 0.2s ease',
                display: 'flex',
                flexDirection: 'column'
              }}
              onMouseEnter={(e) => e.currentTarget.style.boxShadow = '0 4px 20px rgba(0, 229, 255, 0.12)'}
              onMouseLeave={(e) => e.currentTarget.style.boxShadow = 'none'}
            >
              <h3 className="heading-2 mb-3" style={{ color: 'var(--brand-primary)' }}>
                {t('home.metabolicResearch')}
              </h3>
              <p className="body-small mb-4" style={{ color: 'var(--text-light)' }}>
                <span style={{ fontWeight: '600' }}>{t('home.metabolicCompounds')}</span>
              </p>
              <ul className="space-y-2 flex-1">
                <li className="flex items-start gap-2">
                  <span style={{ color: 'var(--brand-primary)', marginTop: '6px', fontSize: '8px' }}>●</span>
                  <span className="body-small" style={{ color: 'var(--text-secondary)' }}>{t('home.metabolicBullet1')}</span>
                </li>
                <li className="flex items-start gap-2">
                  <span style={{ color: 'var(--brand-primary)', marginTop: '6px', fontSize: '8px' }}>●</span>
                  <span className="body-small" style={{ color: 'var(--text-secondary)' }}>{t('home.metabolicBullet2')}</span>
                </li>
                <li className="flex items-start gap-2">
                  <span style={{ color: 'var(--brand-primary)', marginTop: '6px', fontSize: '8px' }}>●</span>
                  <span className="body-small" style={{ color: 'var(--text-secondary)' }}>{t('home.metabolicBullet3')}</span>
                </li>
                <li className="flex items-start gap-2">
                  <span style={{ color: 'var(--brand-primary)', marginTop: '6px', fontSize: '8px' }}>●</span>
                  <span className="body-small" style={{ color: 'var(--text-secondary)' }}>{t('home.metabolicBullet4')}</span>
                </li>
              </ul>
              <Link 
                to="/weight-management" 
                className="inline-flex items-center gap-2 mt-4"
                style={{ 
                  color: 'var(--brand-primary)', 
                  fontWeight: '600', 
                  fontSize: '14px',
                  textDecoration: 'none'
                }}
              >
                {t('home.viewProducts')} <ArrowRight size={16} />
              </Link>
            </div>

            {/* Tile 2: Tissue Repair & Regeneration */}
            <div 
              style={{ 
                background: 'var(--bg-secondary)', 
                border: '1px solid var(--border-light)',
                padding: '28px',
                borderTop: '4px solid var(--brand-primary)',
                transition: 'box-shadow 0.2s ease',
                display: 'flex',
                flexDirection: 'column'
              }}
              onMouseEnter={(e) => e.currentTarget.style.boxShadow = '0 4px 20px rgba(0, 229, 255, 0.12)'}
              onMouseLeave={(e) => e.currentTarget.style.boxShadow = 'none'}
            >
              <h3 className="heading-2 mb-3" style={{ color: 'var(--brand-primary)' }}>
                {t('home.tissueRepair')}
              </h3>
              <p className="body-small mb-4" style={{ color: 'var(--text-light)' }}>
                <span style={{ fontWeight: '600' }}>{t('home.tissueCompounds')}</span>
              </p>
              <ul className="space-y-2 flex-1">
                <li className="flex items-start gap-2">
                  <span style={{ color: 'var(--brand-primary)', marginTop: '6px', fontSize: '8px' }}>●</span>
                  <span className="body-small" style={{ color: 'var(--text-secondary)' }}>{t('home.tissueBullet1')}</span>
                </li>
                <li className="flex items-start gap-2">
                  <span style={{ color: 'var(--brand-primary)', marginTop: '6px', fontSize: '8px' }}>●</span>
                  <span className="body-small" style={{ color: 'var(--text-secondary)' }}>{t('home.tissueBullet2')}</span>
                </li>
                <li className="flex items-start gap-2">
                  <span style={{ color: 'var(--brand-primary)', marginTop: '6px', fontSize: '8px' }}>●</span>
                  <span className="body-small" style={{ color: 'var(--text-secondary)' }}>{t('home.tissueBullet3')}</span>
                </li>
                <li className="flex items-start gap-2">
                  <span style={{ color: 'var(--brand-primary)', marginTop: '6px', fontSize: '8px' }}>●</span>
                  <span className="body-small" style={{ color: 'var(--text-secondary)' }}>{t('home.tissueBullet4')}</span>
                </li>
              </ul>
              <Link 
                to="/tissue-recovery" 
                className="inline-flex items-center gap-2 mt-4"
                style={{ 
                  color: 'var(--brand-primary)', 
                  fontWeight: '600', 
                  fontSize: '14px',
                  textDecoration: 'none'
                }}
              >
                {t('home.viewProducts')} <ArrowRight size={16} />
              </Link>
            </div>

            {/* Tile 3: Neuropeptide Research */}
            <div 
              style={{ 
                background: 'var(--bg-secondary)', 
                border: '1px solid var(--border-light)',
                padding: '28px',
                borderTop: '4px solid var(--brand-primary)',
                transition: 'box-shadow 0.2s ease',
                display: 'flex',
                flexDirection: 'column'
              }}
              onMouseEnter={(e) => e.currentTarget.style.boxShadow = '0 4px 20px rgba(0, 229, 255, 0.12)'}
              onMouseLeave={(e) => e.currentTarget.style.boxShadow = 'none'}
            >
              <h3 className="heading-2 mb-3" style={{ color: 'var(--brand-primary)' }}>
                {t('home.cognitiveNeuro')}
              </h3>
              <p className="body-small mb-4" style={{ color: 'var(--text-light)' }}>
                <span style={{ fontWeight: '600' }}>{t('home.cognitiveCompounds')}</span>
              </p>
              <ul className="space-y-2 flex-1">
                <li className="flex items-start gap-2">
                  <span style={{ color: 'var(--brand-primary)', marginTop: '6px', fontSize: '8px' }}>●</span>
                  <span className="body-small" style={{ color: 'var(--text-secondary)' }}>{t('home.cognitiveBullet1')}</span>
                </li>
                <li className="flex items-start gap-2">
                  <span style={{ color: 'var(--brand-primary)', marginTop: '6px', fontSize: '8px' }}>●</span>
                  <span className="body-small" style={{ color: 'var(--text-secondary)' }}>{t('home.cognitiveBullet2')}</span>
                </li>
                <li className="flex items-start gap-2">
                  <span style={{ color: 'var(--brand-primary)', marginTop: '6px', fontSize: '8px' }}>●</span>
                  <span className="body-small" style={{ color: 'var(--text-secondary)' }}>{t('home.cognitiveBullet3')}</span>
                </li>
                <li className="flex items-start gap-2">
                  <span style={{ color: 'var(--brand-primary)', marginTop: '6px', fontSize: '8px' }}>●</span>
                  <span className="body-small" style={{ color: 'var(--text-secondary)' }}>{t('home.cognitiveBullet4')}</span>
                </li>
              </ul>
              <Link 
                to="/cognitive-neuro" 
                className="inline-flex items-center gap-2 mt-4"
                style={{ 
                  color: 'var(--brand-primary)', 
                  fontWeight: '600', 
                  fontSize: '14px',
                  textDecoration: 'none'
                }}
              >
                {t('home.viewProducts')} <ArrowRight size={16} />
              </Link>
            </div>

            {/* Tile 4: Growth Hormone Axis */}
            <div 
              style={{ 
                background: 'var(--bg-secondary)', 
                border: '1px solid var(--border-light)',
                padding: '28px',
                borderTop: '4px solid var(--brand-primary)',
                transition: 'box-shadow 0.2s ease',
                display: 'flex',
                flexDirection: 'column'
              }}
              onMouseEnter={(e) => e.currentTarget.style.boxShadow = '0 4px 20px rgba(0, 229, 255, 0.12)'}
              onMouseLeave={(e) => e.currentTarget.style.boxShadow = 'none'}
            >
              <h3 className="heading-2 mb-3" style={{ color: 'var(--brand-primary)' }}>
                {t('home.growthHormone')}
              </h3>
              <p className="body-small mb-4" style={{ color: 'var(--text-light)' }}>
                <span style={{ fontWeight: '600' }}>{t('home.growthCompounds')}</span>
              </p>
              <ul className="space-y-2 flex-1">
                <li className="flex items-start gap-2">
                  <span style={{ color: 'var(--brand-primary)', marginTop: '6px', fontSize: '8px' }}>●</span>
                  <span className="body-small" style={{ color: 'var(--text-secondary)' }}>{t('home.growthBullet1')}</span>
                </li>
                <li className="flex items-start gap-2">
                  <span style={{ color: 'var(--brand-primary)', marginTop: '6px', fontSize: '8px' }}>●</span>
                  <span className="body-small" style={{ color: 'var(--text-secondary)' }}>{t('home.growthBullet2')}</span>
                </li>
                <li className="flex items-start gap-2">
                  <span style={{ color: 'var(--brand-primary)', marginTop: '6px', fontSize: '8px' }}>●</span>
                  <span className="body-small" style={{ color: 'var(--text-secondary)' }}>{t('home.growthBullet3')}</span>
                </li>
                <li className="flex items-start gap-2">
                  <span style={{ color: 'var(--brand-primary)', marginTop: '6px', fontSize: '8px' }}>●</span>
                  <span className="body-small" style={{ color: 'var(--text-secondary)' }}>{t('home.growthBullet4')}</span>
                </li>
              </ul>
              <Link 
                to="/growth-hormone" 
                className="inline-flex items-center gap-2 mt-4"
                style={{ color: 'var(--brand-primary)', fontWeight: '600', fontSize: '14px', textDecoration: 'none' }}
              >
                {t('home.viewProducts')} <ArrowRight size={16} />
              </Link>
            </div>

            {/* Tile 5: Cellular & Mitochondrial Research */}
            <div 
              style={{ 
                background: 'var(--bg-secondary)', 
                border: '1px solid var(--border-light)',
                padding: '28px',
                borderTop: '4px solid var(--brand-primary)',
                transition: 'box-shadow 0.2s ease',
                display: 'flex',
                flexDirection: 'column'
              }}
              onMouseEnter={(e) => e.currentTarget.style.boxShadow = '0 4px 20px rgba(0, 229, 255, 0.12)'}
              onMouseLeave={(e) => e.currentTarget.style.boxShadow = 'none'}
            >
              <h3 className="heading-2 mb-3" style={{ color: 'var(--brand-primary)' }}>
                {t('home.combinationResearch')}
              </h3>
              <p className="body-small mb-4" style={{ color: 'var(--text-light)' }}>
                <span style={{ fontWeight: '600' }}>{t('home.combinationCompounds')}</span>
              </p>
              <ul className="space-y-2 flex-1">
                <li className="flex items-start gap-2">
                  <span style={{ color: 'var(--brand-primary)', marginTop: '6px', fontSize: '8px' }}>●</span>
                  <span className="body-small" style={{ color: 'var(--text-secondary)' }}>{t('home.combinationBullet1')}</span>
                </li>
                <li className="flex items-start gap-2">
                  <span style={{ color: 'var(--brand-primary)', marginTop: '6px', fontSize: '8px' }}>●</span>
                  <span className="body-small" style={{ color: 'var(--text-secondary)' }}>{t('home.combinationBullet2')}</span>
                </li>
                <li className="flex items-start gap-2">
                  <span style={{ color: 'var(--brand-primary)', marginTop: '6px', fontSize: '8px' }}>●</span>
                  <span className="body-small" style={{ color: 'var(--text-secondary)' }}>{t('home.combinationBullet3')}</span>
                </li>
                <li className="flex items-start gap-2">
                  <span style={{ color: 'var(--brand-primary)', marginTop: '6px', fontSize: '8px' }}>●</span>
                  <span className="body-small" style={{ color: 'var(--text-secondary)' }}>{t('home.combinationBullet4')}</span>
                </li>
              </ul>
              <Link 
                to="/blends" 
                className="inline-flex items-center gap-2 mt-4"
                style={{ color: 'var(--brand-primary)', fontWeight: '600', fontSize: '14px', textDecoration: 'none' }}
              >
                {t('home.viewProducts')} <ArrowRight size={16} />
              </Link>
            </div>

            {/* Tile 6: Immunomodulation Research */}
            <div 
              style={{ 
                background: 'var(--bg-secondary)', 
                border: '1px solid var(--border-light)',
                padding: '28px',
                borderTop: '4px solid var(--brand-primary)',
                transition: 'box-shadow 0.2s ease',
                display: 'flex',
                flexDirection: 'column'
              }}
              onMouseEnter={(e) => e.currentTarget.style.boxShadow = '0 4px 20px rgba(0, 229, 255, 0.12)'}
              onMouseLeave={(e) => e.currentTarget.style.boxShadow = 'none'}
            >
              <h3 className="heading-2 mb-3" style={{ color: 'var(--brand-primary)' }}>
                {t('home.labSupplies')}
              </h3>
              <p className="body-small mb-4" style={{ color: 'var(--text-light)' }}>
                <span style={{ fontWeight: '600' }}>{t('home.labCompounds')}</span>
              </p>
              <ul className="space-y-2 flex-1">
                <li className="flex items-start gap-2">
                  <span style={{ color: 'var(--brand-primary)', marginTop: '6px', fontSize: '8px' }}>●</span>
                  <span className="body-small" style={{ color: 'var(--text-secondary)' }}>{t('home.labBullet1')}</span>
                </li>
                <li className="flex items-start gap-2">
                  <span style={{ color: 'var(--brand-primary)', marginTop: '6px', fontSize: '8px' }}>●</span>
                  <span className="body-small" style={{ color: 'var(--text-secondary)' }}>{t('home.labBullet2')}</span>
                </li>
                <li className="flex items-start gap-2">
                  <span style={{ color: 'var(--brand-primary)', marginTop: '6px', fontSize: '8px' }}>●</span>
                  <span className="body-small" style={{ color: 'var(--text-secondary)' }}>{t('home.labBullet3')}</span>
                </li>
                <li className="flex items-start gap-2">
                  <span style={{ color: 'var(--brand-primary)', marginTop: '6px', fontSize: '8px' }}>●</span>
                  <span className="body-small" style={{ color: 'var(--text-secondary)' }}>{t('home.labBullet4')}</span>
                </li>
              </ul>
              <Link 
                to="/lab-supplies" 
                className="inline-flex items-center gap-2 mt-4"
                style={{ color: 'var(--brand-primary)', fontWeight: '600', fontSize: '14px', textDecoration: 'none' }}
              >
                {t('home.viewProducts')} <ArrowRight size={16} />
              </Link>
            </div>
          </div>

          {/* Compliance Line */}
          <div className="text-center mt-10">
            <p 
              className="body-small" 
              style={{ 
                color: 'var(--text-light)', 
                fontStyle: 'italic',
                maxWidth: '700px',
                margin: '0 auto',
                paddingTop: '16px',
                borderTop: '1px solid var(--border-light)'
              }}
            >
              {t('home.complianceLine')}
            </p>
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      {reviews.length > 0 && (
        <section className="section-padding rcm-section reveal">
          <div className="container">
            <div className="text-center mb-12">
              <h2 className="hero-medium mb-4" style={{ color: 'var(--brand-primary)' }}>
                {t('home.reviewsTitle')}
              </h2>
              <div className="flex items-center justify-center gap-3 mb-2">
                <StarRating rating={Math.round(reviewStats.average_rating)} size={24} />
                <span className="heading-3" style={{ color: 'var(--brand-primary)' }}>
                  {reviewStats.average_rating.toFixed(1)}
                </span>
              </div>
              <p className="body-regular" style={{ color: 'var(--text-secondary)' }}>
                {t('home.basedOn')} {reviewStats.total_reviews} {t('home.reviews')}
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
              {reviews.map((review) => {
                // Rating-only reviews (no written comment) leave `review.review`
                // as an empty string or null. Historically we rendered
                // "{empty}" which literally shows a pair of quote characters
                // — bad UX. Guard the comment block so empty comments render
                // NOTHING (no quotes, no whitespace) and star-only reviews
                // still show name + stars + Verified pill cleanly.
                const commentText = typeof review.review === 'string' ? review.review.trim() : '';
                const hasComment = commentText.length > 0;
                const displayComment = hasComment
                  ? (commentText.length > 150 ? `${commentText.substring(0, 150)}…` : commentText)
                  : '';
                // Use the review's OWN stored rating; never hardcode 5.
                const numericRating = Number(review.rating) || 0;
                return (
                <div
                  key={review.id}
                  data-testid="home-review-card"
                  style={{
                    background: 'var(--bg-primary)',
                    border: '1px solid var(--border-light)',
                    padding: '24px'
                  }}
                >
                  <StarRating rating={numericRating} size={18} />
                  {hasComment && (
                    <p
                      className="body-regular my-4"
                      style={{ color: 'var(--text-secondary)', lineHeight: '1.6' }}
                      data-testid="home-review-comment"
                    >
                      &ldquo;{displayComment}&rdquo;
                    </p>
                  )}
                  <div className={hasComment ? '' : 'mt-4'}>
                    <p className="body-small" style={{ color: 'var(--brand-primary)', fontWeight: '600', margin: 0 }}>
                      — {review.name}
                    </p>
                    <p
                      className="body-small"
                      style={{ color: '#10b981', fontWeight: 500, fontSize: '12px', marginTop: '4px' }}
                      data-testid="home-review-verified"
                    >
                      ✓ Verified Customer
                    </p>
                  </div>
                </div>
                );
              })}
            </div>

            <div className="text-center">
              <Link 
                to="/reviews" 
                className="btn-secondary inline-flex items-center gap-2"
                style={{ borderColor: 'var(--brand-primary)', color: 'var(--brand-primary)' }}
              >
                {t('home.seeAllReviews')}
                <ArrowRight size={18} />
              </Link>
            </div>
          </div>
        </section>
      )}

      {/* Research Tools Section removed 2026-02 —
          the reconstitution/dose-preparation calculator has
          been taken down; nothing here should link researchers
          to a syringe-unit tool. Do not re-add without a
          product decision. */}

      {/* Community Chat Section — disabled, set REACT_APP_COMMUNITY_ENABLED=true to re-enable */}
      {process.env.REACT_APP_COMMUNITY_ENABLED === 'true' && (
      <section className="section-padding rcm-section-alt reveal" data-testid="community-chat-section">
        <div className="container">
          {/* Welcome Header */}
          <div className="text-center mb-8">
            <div style={{ display: 'inline-flex', alignItems: 'center', gap: '10px', background: 'rgba(20,184,166,0.15)', padding: '8px 20px', borderRadius: '24px', marginBottom: '16px' }}>
              <MessageCircle size={18} style={{ color: '#00E5FF' }} />
              <span style={{ color: '#00E5FF', fontWeight: '600', fontSize: '14px', letterSpacing: '0.5px' }}>{t('home.communityBadge')}</span>
            </div>
            <h2 className="hero-medium mb-4" style={{ color: '#ffffff' }}>
              {t('home.welcomeCommunity')}
            </h2>
            <p className="body-large" style={{ color: 'rgba(255,255,255,0.7)', maxWidth: '620px', margin: '0 auto 8px' }}>
              {t('home.welcomeDesc')}
            </p>
            <p style={{ color: 'rgba(255,255,255,0.55)', fontSize: '15px', maxWidth: '580px', margin: '0 auto' }}>
              {t('home.welcomeDesc2')}
            </p>
          </div>

          {/* Info Cards Row */}
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))',
            gap: '20px',
            maxWidth: '900px',
            margin: '0 auto 40px'
          }}>
            {/* First Things First */}
            <div data-testid="community-intro-card" style={{
              background: 'rgba(255,255,255,0.06)',
              border: '1px solid rgba(20,184,166,0.25)',
              borderRadius: '12px',
              padding: '24px'
            }}>
              <h3 style={{ color: '#00E5FF', fontSize: '16px', fontWeight: '600', marginBottom: '12px' }}>
                {t('home.firstThings')}
              </h3>
              <p style={{ color: 'rgba(255,255,255,0.7)', fontSize: '14px', lineHeight: '1.6', marginBottom: '10px' }}>
                {t('home.firstThingsDesc')}
              </p>
              <ul style={{ margin: 0, paddingLeft: '18px' }}>
                <li style={{ color: 'rgba(255,255,255,0.65)', fontSize: '14px', lineHeight: '1.8' }}>{t('home.yourName')}</li>
                <li style={{ color: 'rgba(255,255,255,0.65)', fontSize: '14px', lineHeight: '1.8' }}>{t('home.whereFrom')}</li>
                <li style={{ color: 'rgba(255,255,255,0.65)', fontSize: '14px', lineHeight: '1.8' }}>{t('home.whatBrought')}</li>
              </ul>
              <p style={{ color: 'rgba(255,255,255,0.5)', fontSize: '13px', marginTop: '10px', fontStyle: 'italic' }}>{t('home.loveMeeting')}</p>
            </div>

            {/* Things You Can Do */}
            <div data-testid="community-activities-card" style={{
              background: 'rgba(255,255,255,0.06)',
              border: '1px solid rgba(255,255,255,0.1)',
              borderRadius: '12px',
              padding: '24px'
            }}>
              <h3 style={{ color: '#fff', fontSize: '16px', fontWeight: '600', marginBottom: '12px' }}>
                {t('home.thingsYouCan')}
              </h3>
              <p style={{ color: 'rgba(255,255,255,0.7)', fontSize: '14px', lineHeight: '1.6', marginBottom: '10px' }}>
                {t('home.insideCommunity')}
              </p>
              <ul style={{ margin: 0, paddingLeft: '18px' }}>
                <li style={{ color: 'rgba(255,255,255,0.65)', fontSize: '14px', lineHeight: '1.8' }}>{t('home.askQuestions')}</li>
                <li style={{ color: 'rgba(255,255,255,0.65)', fontSize: '14px', lineHeight: '1.8' }}>{t('home.shareProgress')}</li>
                <li style={{ color: 'rgba(255,255,255,0.65)', fontSize: '14px', lineHeight: '1.8' }}>{t('home.stayMotivated')}</li>
                <li style={{ color: 'rgba(255,255,255,0.65)', fontSize: '14px', lineHeight: '1.8' }}>{t('home.connectOthers')}</li>
              </ul>
            </div>

            {/* Quick Reminders */}
            <div data-testid="community-reminders-card" style={{
              background: 'rgba(255,255,255,0.06)',
              border: '1px solid rgba(255,255,255,0.1)',
              borderRadius: '12px',
              padding: '24px'
            }}>
              <h3 style={{ color: '#fff', fontSize: '16px', fontWeight: '600', marginBottom: '12px' }}>
                {t('home.quickReminders')}
              </h3>
              <ul style={{ margin: '0 0 12px', paddingLeft: '18px' }}>
                <li style={{ color: 'rgba(255,255,255,0.65)', fontSize: '14px', lineHeight: '1.8' }}>{t('home.beRespectful')}</li>
                <li style={{ color: 'rgba(255,255,255,0.65)', fontSize: '14px', lineHeight: '1.8' }}>{t('home.noSpam')}</li>
                <li style={{ color: 'rgba(255,255,255,0.65)', fontSize: '14px', lineHeight: '1.8' }}>{t('home.noMedicalAdvice')}</li>
                <li style={{ color: 'rgba(255,255,255,0.65)', fontSize: '14px', lineHeight: '1.8' }}>{t('home.protectPrivacy')}</li>
              </ul>
              <Link to="/community/guidelines" style={{ color: '#00E5FF', fontSize: '14px', textDecoration: 'none', fontWeight: '500' }}>
                {t('home.readGuidelines')} →
              </Link>
            </div>
          </div>

          {/* Sign In Prompt (for non-authenticated users) */}
          {!isAuthenticated && (
            <div data-testid="community-login-instructions" className="text-center" style={{ marginBottom: '32px' }}>
              <div style={{
                maxWidth: '480px',
                margin: '0 auto',
                background: 'rgba(20,184,166,0.1)',
                border: '1px solid rgba(20,184,166,0.3)',
                borderRadius: '12px',
                padding: '24px'
              }}>
                <h3 style={{ color: '#00E5FF', fontSize: '16px', fontWeight: '600', marginBottom: '10px', display: 'flex', alignItems: 'center', gap: '8px', justifyContent: 'center' }}>
                  <LogIn size={18} />
                  {t('home.howToJoin')}
                </h3>
                <p style={{ color: 'rgba(255,255,255,0.7)', fontSize: '14px', marginBottom: '16px' }}>
                  {t('home.howToJoinDesc')}
                </p>
                <button
                  data-testid="community-sign-in-btn"
                  onClick={() => setShowAuthModal(true)}
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '8px',
                    background: '#00E5FF',
                    color: '#000',
                    border: 'none',
                    padding: '12px 28px',
                    borderRadius: '8px',
                    cursor: 'pointer',
                    fontWeight: '600',
                    fontSize: '15px'
                  }}
                >
                  <LogIn size={18} />
                  {t('home.signInToChat')}
                </button>
              </div>
            </div>
          )}

          {/* Chat Rooms Grid */}
          {chatRooms.length > 0 ? (
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
              gap: '20px',
              maxWidth: '1000px',
              margin: '0 auto 32px'
            }}>
              {chatRooms.map((room) => (
                <div
                  key={room.id}
                  data-testid={`home-chat-room-${room.id}`}
                  onClick={() => {
                    if (!isAuthenticated) {
                      setShowAuthModal(true);
                    } else {
                      const token = localStorage.getItem('auth_token');
                      fetch(`${API_URL}/api/chat/rooms/${room.id}/join`, {
                        method: 'POST',
                        headers: { 'Authorization': `Bearer ${token}` }
                      }).then(() => navigate(`/chat/${room.id}`))
                        .catch(() => navigate(`/chat/${room.id}`));
                    }
                  }}
                  style={{
                    background: 'rgba(255,255,255,0.05)',
                    borderRadius: '14px',
                    padding: '24px',
                    border: '1px solid rgba(255,255,255,0.1)',
                    cursor: 'pointer',
                    transition: 'all 0.3s ease'
                  }}
                  onMouseEnter={(e) => { e.currentTarget.style.background = 'rgba(255,255,255,0.1)'; e.currentTarget.style.borderColor = '#00E5FF'; }}
                  onMouseLeave={(e) => { e.currentTarget.style.background = 'rgba(255,255,255,0.05)'; e.currentTarget.style.borderColor = 'rgba(255,255,255,0.1)'; }}
                >
                  <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: '14px' }}>
                    <div style={{
                      width: '44px',
                      height: '44px',
                      borderRadius: '10px',
                      background: 'rgba(0, 229, 255, 0.15)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}>
                      <MessageCircle size={22} color="white" />
                    </div>
                    {room.is_private ? (
                      <Lock size={16} style={{ color: 'rgba(255,255,255,0.4)' }} />
                    ) : (
                      <Globe size={16} style={{ color: '#00E5FF' }} />
                    )}
                  </div>
                  <h3 style={{ color: '#fff', fontSize: '17px', marginBottom: '6px', fontWeight: '600' }}>
                    {room.name}
                  </h3>
                  <p style={{ color: 'rgba(255,255,255,0.5)', fontSize: '13px', marginBottom: '14px', minHeight: '36px' }}>
                    {room.description || t('home.joinDiscussion')}
                  </p>
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    paddingTop: '14px',
                    borderTop: '1px solid rgba(255,255,255,0.08)'
                  }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px', color: 'rgba(255,255,255,0.5)' }}>
                      <Users size={14} />
                      <span style={{ fontSize: '13px' }}>{room.member_count || 0} {t('home.members')}</span>
                    </div>
                    <ArrowRight size={16} style={{ color: '#00E5FF' }} />
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div style={{
              textAlign: 'center',
              padding: '40px',
              background: 'rgba(255,255,255,0.03)',
              borderRadius: '16px',
              border: '1px solid rgba(255,255,255,0.1)',
              maxWidth: '500px',
              margin: '0 auto 32px'
            }}>
              <MessageCircle size={40} style={{ color: 'rgba(255,255,255,0.3)', marginBottom: '12px' }} />
              <p style={{ color: 'rgba(255,255,255,0.6)', fontSize: '15px' }}>{t('home.chatComingSoon')}</p>
            </div>
          )}

          {/* View All Button */}
          <div className="text-center">
            <Link
              to="/community"
              data-testid="view-all-community-btn"
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '8px',
                color: '#00E5FF',
                fontSize: '15px',
                fontWeight: '600',
                textDecoration: 'none',
                padding: '10px 24px',
                border: '1px solid rgba(20,184,166,0.4)',
                borderRadius: '8px',
                transition: 'all 0.3s ease'
              }}
            >
              {t('home.viewAllRooms')}
              <ArrowRight size={18} />
            </Link>
            <Link
              to="/community/guidelines"
              data-testid="community-guidelines-link"
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '8px',
                color: 'rgba(255,255,255,0.5)',
                fontSize: '14px',
                textDecoration: 'none',
                marginLeft: '16px'
              }}
            >
              {t('home.communityGuidelines')}
            </Link>
          </div>
        </div>
      </section>
      )}

      {/* Auth Modal for Community Sign In */}
      <AuthModal isOpen={showAuthModal} onClose={() => setShowAuthModal(false)} />

      {/* ─── SEO / AEO body sections ─────────────────────────────────────
          Visible copy that (a) reinforces the topical focus for search
          engines and answer engines, (b) mirrors the prerendered body in
          page-content.js so runtime + SSR content stay in sync, and
          (c) surfaces internal links to canonical routes. No hidden
          keyword lists — every phrase reads as normal descriptive copy.
      */}
      <section className="section-padding reveal" style={{ background: 'var(--bg-secondary)' }} data-testid="home-catalog-seo">
        <div className="container">
          <div style={{ maxWidth: '820px', margin: '0 auto' }}>
            <h2 className="hero-medium mb-4" style={{ color: '#ffffff' }}>Laboratory Research Peptide Catalog</h2>
            <p className="body-large mb-6" style={{ color: 'rgba(255,255,255,0.85)' }}>
              Explore high-purity laboratory research peptides, bioscience compounds, research blends, and laboratory supplies organized by research category. Product pages include available specifications and batch-specific Certificate of Analysis information where documented.
            </p>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '12px 24px' }}>
              <Link to="/products" data-testid="home-seo-link-catalog" style={{ color: '#00E5FF', textDecoration: 'none', fontWeight: 600, fontSize: '0.95rem' }}>
                Browse the Research Peptide Catalog →
              </Link>
              <Link to="/research-peptide-guide" data-testid="home-seo-link-guide" style={{ color: '#00E5FF', textDecoration: 'none', fontWeight: 600, fontSize: '0.95rem' }}>
                Read the Research Peptide Guide →
              </Link>
            </div>
          </div>
        </div>
      </section>

      <section className="section-padding reveal" style={{ background: 'var(--bg-primary)' }} data-testid="home-quality-seo">
        <div className="container">
          <div style={{ maxWidth: '820px', margin: '0 auto' }}>
            <h2 className="hero-medium mb-4" style={{ color: '#ffffff' }}>Third-Party Tested Research Peptides</h2>
            <p className="body-large mb-6" style={{ color: 'rgba(255,255,255,0.85)' }}>
              Third-party tested and batch verified, with product-specific purity, identity, lot, and Certificate of Analysis documentation available where applicable. Testing information reflects the documentation provided for each product and batch.
            </p>
            <Link to="/quality" data-testid="home-seo-link-quality" style={{ color: '#00E5FF', textDecoration: 'none', fontWeight: 600, fontSize: '0.95rem' }}>
              View Quality and Testing Standards →
            </Link>
            <p style={{ color: 'rgba(255,255,255,0.55)', fontSize: '0.85rem', marginTop: '28px', letterSpacing: '0.02em' }}>
              Research use only. Not for human or animal use. Not for injection.
            </p>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="section-padding rcm-section reveal">
        <div className="container">
          <div className="text-center" style={{ maxWidth: '800px', margin: '0 auto' }}>
            <h2 className="hero-medium mb-6" style={{ color: '#ffffff' }}>{t('home.ctaTitle')}</h2>
            <p className="body-large mb-8" style={{ color: 'rgba(255,255,255,0.85)' }}>
              {t('home.ctaDesc')}
            </p>
            <Link to="/contact" className="btn-primary" style={{ background: '#00E5FF', color: '#000', borderColor: '#00E5FF' }}>
              {t('home.submitRequest')}
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;
